import { EPaletteModes } from '@/types/theme-packs.types';
import {
  parseColor,
  resolveColor,
  eventImage,
  formatTimestamp,
  getHeaderImage,
  getBtvFallbackForBg,
} from './utils/utils';
import type { TemplateProps } from './index';
import chroma from 'chroma-js';
import ContentEditable from './content-editable';
import { useImageUpload } from './utils/use-image-upload';
import styles from './template-styles.module.css';
import getRelativeContrastColor from '@/utils/colors/generate-relative-contrasting-color';

export const Green = ({
  event,
  theme,
  mode,
  calendarLinks,
  customizations,
  href,
  salutation,
  setSalutation,
  title,
  setTitle,
  description,
  setDescription,
  cta,
  setCta,
  setMainImage,
  setHeaderImage,
}: TemplateProps) => {
  const resolvedMode = mode ?? event.settings.design.color_theme ?? theme.theme_mode ?? EPaletteModes.Light;
  const baseBg = resolvedMode === EPaletteModes.Dark ? '#000000' : '#FFFFFF';
  const themeColors = theme.colors[resolvedMode] ?? theme.colors;
  const bg =
    customizations?.backgroundColor ||
    chroma
      .mix(
        baseBg,
        customizations?.bodyTextColor || parseColor(themeColors.bodyTextColor, { opacity: 0.5 }),
        0.05,
        'rgb'
      )
      .css();
  const container = resolveColor(themeColors.containerColor, customizations?.containerColor);
  const heading = getRelativeContrastColor(
    resolveColor(themeColors.headingTextColor, customizations?.headingTextColor),
    container
  );
  const buttonText = getRelativeContrastColor(
    resolveColor(themeColors.buttonTextColor ?? themeColors.bodyTextColor, customizations?.buttonTextColor),
    heading
  );
  const [loadingMain, uploadMain] = useImageUpload(setMainImage);
  const [loadingHeaderImg, uploadHeaderImg] = useImageUpload(setHeaderImage);
  const sessionDate = event.sessions[0]?.timestamp ? formatTimestamp(event.sessions[0].timestamp) : null;
  const anchorHref = href || '#';
  const headerImg = customizations?.headingImage || getHeaderImage(event) || getBtvFallbackForBg(container);
  const bodyImg = customizations?.mainImage || eventImage(event);
  const headingFont = customizations?.headingFont ? `${customizations.headingFont}, sans-serif` : 'Georgia, serif';
  const bodyFont = customizations?.bodyFont ? `${customizations.bodyFont}, sans-serif` : 'Trebuchet MS, sans-serif';
  const _salutation = salutation ?? (customizations?.salutation || 'You have been invited to');
  const eventTitle = title ?? (customizations?.title || event.name);
  const eventDescription = description ?? customizations?.description;
  const _cta = cta ?? (customizations?.cta || 'REGISTER');

  return (
    <table
      data-component="Green"
      width="100%"
      cellPadding={0}
      cellSpacing={0}
      border={0}
      role="presentation"
      className={styles.email}
      style={
        {
          '--bg': bg,
          '--container': container,
          '--heading': heading,
          '--button-text': buttonText,
          '--heading-font': headingFont,
          '--body-font': bodyFont,
          background: 'var(--bg)',
        } as React.CSSProperties
      }
    >
      <tbody>
        <tr>
          <td align="center" style={{ padding: '40px 40px 24px 40px' }}>
            <table
              width={520}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '520px', background: 'var(--container)' }}
            >
              <tbody>
                {/* Logo header */}
                <tr>
                  <td align="center" valign="middle" height={48} style={{ padding: '8px' }}>
                    {headerImg && (
                      <div
                        onClick={uploadHeaderImg}
                        data-cont="img"
                        className={loadingHeaderImg ? styles.imgLoading : undefined}
                      >
                        <img height={32} src={headerImg} style={{ display: 'block', margin: '0 auto' }} alt="" />
                      </div>
                    )}
                  </td>
                </tr>

                {/* First hero image */}
                <tr>
                  <td style={{ fontSize: 0, lineHeight: '0' }}>
                    {bodyImg && (
                      <div onClick={uploadMain} data-cont="img" className={loadingMain ? styles.imgLoading : undefined}>
                        <img width={348} src={bodyImg} style={{ display: 'block' }} alt="" />
                      </div>
                    )}
                  </td>
                </tr>

                {/* Invite label */}
                <tr>
                  <td align="center" style={{ padding: '24px 8px 8px 8px' }}>
                    <ContentEditable
                      value={_salutation}
                      setValue={setSalutation}
                      style={{
                        color: 'var(--heading)',
                        fontSize: '14px',
                        fontFamily: 'var(--body-font)',
                        fontWeight: 400,
                        lineHeight: '11.2px',
                        wordWrap: 'break-word',
                        textAlign: 'center',
                      }}
                    />
                  </td>
                </tr>

                {/* Title */}
                <tr>
                  <td align="center" style={{ padding: '0 8px 24px 8px' }}>
                    <ContentEditable
                      value={eventTitle}
                      setValue={setTitle}
                      style={{
                        color: 'var(--heading)',
                        fontSize: '56px',
                        fontFamily: 'var(--heading-font)',
                        fontWeight: 400,
                        textTransform: 'capitalize',
                        lineHeight: '56px',
                        wordWrap: 'break-word',
                        textAlign: 'center',
                        maxWidth: '492px',
                      }}
                    />
                  </td>
                </tr>

                {eventDescription && (
                  <tr>
                    <td align="center" style={{ padding: '0 8px 16px 8px' }}>
                      <ContentEditable
                        value={eventDescription}
                        setValue={setDescription}
                        style={{
                          color: 'var(--heading)',
                          fontSize: '14px',
                          fontFamily: 'var(--body-font)',
                          fontWeight: 400,
                          lineHeight: '20px',
                          wordWrap: 'break-word',
                          textAlign: 'center',
                        }}
                      />
                    </td>
                  </tr>
                )}

                {/* Second hero image (right-aligned) */}
                <tr>
                  <td align="right" style={{ fontSize: 0, lineHeight: '0' }}>
                    {bodyImg && (
                      <div onClick={uploadMain} data-cont="img" className={loadingMain ? styles.imgLoading : undefined}>
                        <img width={313} src={bodyImg} style={{ display: 'block', marginLeft: 'auto' }} alt="" />
                      </div>
                    )}
                  </td>
                </tr>

                {/* Date badge or spacer */}
                {sessionDate ? (
                  <tr>
                    <td align="center" style={{ padding: '40px 24px 24px 24px' }}>
                      <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                        <tbody>
                          <tr>
                            <td align="center" style={{ border: '1px solid var(--heading)', padding: '8px 16px' }}>
                              <span
                                style={{
                                  color: 'var(--heading)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 500,
                                  textTransform: 'uppercase',
                                  lineHeight: '12px',
                                  letterSpacing: '0.24px',
                                  wordWrap: 'break-word',
                                }}
                              >
                                {sessionDate}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td style={{ padding: '40px 24px 24px 24px', fontSize: 0, lineHeight: '0' }}>&nbsp;</td>
                  </tr>
                )}

                {/* Register button */}
                <tr>
                  <td align="center" style={{ paddingBottom: '32px' }}>
                    <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                      <tbody>
                        <tr>
                          <td
                            align="center"
                            valign="middle"
                            style={{ background: 'var(--heading)', width: '148px', height: '40px' }}
                          >
                            <a
                              href={anchorHref}
                              onClick={(e) => e.preventDefault()}
                              style={{
                                textDecoration: 'none',
                                padding: '6px 0 6px 0',
                                width: '148px',
                                height: '100%',
                                textAlign: 'center',
                              }}
                            >
                              <ContentEditable
                                value={_cta}
                                setValue={setCta}
                                style={{
                                  color: 'var(--button-text)',
                                  fontSize: '13px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 700,
                                  lineHeight: '16px',
                                  wordWrap: 'break-word',
                                }}
                              />
                            </a>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Calendar links footer */}
            <table cellPadding={0} cellSpacing={0} border={0} role="presentation" style={{ marginTop: '40px' }}>
              <tbody>
                {!!calendarLinks && (
                  <tr>
                    <td>
                      <a
                        style={{
                          color: 'var(--heading)',
                          fontSize: '12px',
                          fontFamily: 'var(--body-font)',
                          fontWeight: 600,
                          lineHeight: '20px',
                          letterSpacing: '0.24px',
                        }}
                        href={calendarLinks.iCal}
                      >
                        iCal
                      </a>
                    </td>
                    <td
                      style={{
                        padding: '0 4px',
                        color: 'var(--heading)',
                        fontSize: '11px',
                        fontFamily: "'TASA Orbiter', sans-serif",
                        fontWeight: 600,
                        lineHeight: '16px',
                        letterSpacing: '0.22px',
                      }}
                    >
                      •
                    </td>
                    <td>
                      <a
                        style={{
                          color: 'var(--heading)',
                          fontSize: '12px',
                          fontFamily: 'var(--body-font)',
                          fontWeight: 600,
                          lineHeight: '20px',
                          letterSpacing: '0.24px',
                        }}
                        href={calendarLinks.outlook}
                      >
                        Outlook
                      </a>
                    </td>
                    <td
                      style={{
                        padding: '0 4px',
                        color: 'var(--heading)',
                        fontSize: '11px',
                        fontFamily: "'TASA Orbiter', sans-serif",
                        fontWeight: 600,
                        lineHeight: '16px',
                        letterSpacing: '0.22px',
                      }}
                    >
                      •
                    </td>
                    <td>
                      <a
                        style={{
                          color: 'var(--heading)',
                          fontSize: '12px',
                          fontFamily: 'var(--body-font)',
                          fontWeight: 600,
                          lineHeight: '20px',
                          letterSpacing: '0.24px',
                        }}
                        href={calendarLinks.google}
                      >
                        Google
                      </a>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
};
